import type { AnalyticsExportFormat } from '../components/analytics/AnalyticsExportMenu';
import type {
  ReportDeliveryChartPoint,
  ReportDeliveryDetailRow,
  ReportDeliveryModalityRow,
  ReportDeliverySummary,
} from '../services/reportDeliveryAnalyticsService';
import { openAnalyticsPrintWindow } from './analyticsExport';
import { formatDisplayDate, formatGeneratedAt } from './billingReconciliationExport';

/**
 * Filter / period metadata shown in exported report headers.
 * Label fields are optional human-readable values already resolved from dashboard filters.
 */
export interface ReportDeliveryExportMeta {
  /** Inclusive ISO date `YYYY-MM-DD` for the start of the report period. */
  startDate: string;
  /** Inclusive ISO date `YYYY-MM-DD` for the end of the report period. */
  endDate: string;
  facilityLabel?: string;
  modalityLabel?: string;
  priorityLabel?: string;
  radiologistLabel?: string;
  deliveryChannelLabel?: string;
}

/**
 * Dashboard snapshot passed into {@link exportReportDeliveryAnalytics}.
 *
 * - `summary` — KPI cards (studies, delivered, avg time, delayed).
 * - `modalityRows` — per-modality totals previously exported via `exportModalityCsv`
 *   (columns: modality, totalReports, avgDeliveryTimeFormatted).
 * - `chartPoints` — optional daily avg delivery minutes series.
 * - `detailRows` — per-study export rows (UHID, timestamps, TAT columns).
 */
export interface ReportDeliveryExportData {
  summary: ReportDeliverySummary;
  modalityRows: ReportDeliveryModalityRow[];
  chartPoints?: ReportDeliveryChartPoint[];
  detailRows?: ReportDeliveryDetailRow[];
}

function escapeCsv(value: string | number): string {
  return `"${String(value).replace(/"/g, '""')}"`;
}

function escapeHtml(value: string | number): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatPeriodLabel(startDate: string, endDate: string): string {
  const start = formatDisplayDate(startDate);
  const end = formatDisplayDate(endDate);
  return startDate === endDate ? start : `${start} – ${end}`;
}

function formatExportDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
}

/**
 * Creates a Blob download and always revokes the object URL after click to avoid leaks.
 */
function downloadBlob(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function buildModalityTableRows(rows: ReportDeliveryModalityRow[]): string {
  if (!rows.length) {
    return '<tr><td colspan="3">No data for the selected filters.</td></tr>';
  }
  return rows
    .map(
      row =>
        `<tr><td>${escapeHtml(row.modality)}</td><td>${escapeHtml(row.totalReports)}</td><td>${escapeHtml(row.avgDeliveryTimeFormatted)}</td></tr>`
    )
    .join('');
}

function buildChartTableRows(points: ReportDeliveryChartPoint[]): string {
  if (!points?.length) {
    return '<tr><td colspan="2">No chart data for the selected date range.</td></tr>';
  }
  return points
    .map(
      point =>
        `<tr><td>${escapeHtml(point.date)}</td><td>${escapeHtml(point.minutes)} min</td></tr>`
    )
    .join('');
}

function buildDetailTableRows(rows: ReportDeliveryDetailRow[]): string {
  if (!rows?.length) {
    return '<tr><td colspan="9">No study data for the selected filters.</td></tr>';
  }
  return rows
    .map(
      row =>
        `<tr>
          <td>${escapeHtml(row.uhid || '—')}</td>
          <td>${escapeHtml(row.patientVisitId || '—')}</td>
          <td>${escapeHtml(row.modality || '—')}</td>
          <td>${escapeHtml(formatExportDateTime(row.studyAllocationTime))}</td>
          <td>${escapeHtml(formatExportDateTime(row.reportDraftTime))}</td>
          <td>${escapeHtml(formatExportDateTime(row.approvalTime))}</td>
          <td>${escapeHtml(formatExportDateTime(row.dispatchTime))}</td>
          <td>${escapeHtml(row.totalDeliveryTatFormatted || '—')}</td>
          <td>${escapeHtml(row.endToEndTatFormatted || '—')}</td>
        </tr>`
    )
    .join('');
}

const DETAIL_CSV_HEADERS = [
  'UHID',
  'Patient ID/ Visit ID',
  'Modality',
  'Study allocation Time',
  'Report Draft Time',
  'Approval Time',
  'Dispatch Time',
  'Total Delivery TAT',
  'End-to-End TAT',
] as const;

function buildDetailCsvRows(rows: ReportDeliveryDetailRow[]): string[] {
  if (!rows?.length) {
    return ['No study data,,,,,,,,'];
  }
  return rows.map(row =>
    [
      escapeCsv(row.uhid || '—'),
      escapeCsv(row.patientVisitId || '—'),
      escapeCsv(row.modality || '—'),
      escapeCsv(formatExportDateTime(row.studyAllocationTime)),
      escapeCsv(formatExportDateTime(row.reportDraftTime)),
      escapeCsv(formatExportDateTime(row.approvalTime)),
      escapeCsv(formatExportDateTime(row.dispatchTime)),
      escapeCsv(row.totalDeliveryTatFormatted || '—'),
      escapeCsv(row.endToEndTatFormatted || '—'),
    ].join(',')
  );
}

function buildReportHtml(data: ReportDeliveryExportData, meta: ReportDeliveryExportMeta): string {
  const { summary, modalityRows, chartPoints = [], detailRows = [] } = data;
  const filterRows = [
    ['Report period', formatPeriodLabel(meta.startDate, meta.endDate)],
    meta.facilityLabel ? ['Facility', meta.facilityLabel] : null,
    meta.modalityLabel ? ['Modality', meta.modalityLabel] : null,
    meta.priorityLabel ? ['Priority', meta.priorityLabel] : null,
    meta.radiologistLabel ? ['Radiologist', meta.radiologistLabel] : null,
    meta.deliveryChannelLabel ? ['Delivery channel', meta.deliveryChannelLabel] : null,
    ['Generated on', formatGeneratedAt()],
  ]
    .filter((row): row is [string, string] => Array.isArray(row))
    .map(([label, value]) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(value)}</td></tr>`)
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Report Delivery Analytics</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; margin: 0; padding: 24px; background: #f8fafc; }
    .report { max-width: 960px; margin: 0 auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
    .report-header { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #fff; padding: 24px 28px; }
    .report-header h1 { margin: 0 0 8px; font-size: 24px; font-weight: 700; }
    .report-body { padding: 24px 28px 32px; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 24px; }
    .summary-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; background: #f8fafc; }
    .summary-card .label { font-size: 12px; color: #64748b; margin-bottom: 6px; }
    .summary-card .value { font-size: 18px; font-weight: 700; color: #0f172a; }
    h2 { font-size: 16px; margin: 24px 0 10px; color: #0f172a; border-bottom: 2px solid #2563eb; padding-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; }
    th { background: #f8fafc; font-weight: 600; }
    .detail-table { font-size: 12px; }
    .detail-table th, .detail-table td { white-space: nowrap; }
  </style>
</head>
<body>
  <div class="report">
    <div class="report-header">
      <h1>Report Delivery Analytics</h1>
      <div>Export generated from dashboard filters</div>
    </div>
    <div class="report-body">
      <h2>Filters</h2>
      <table><tbody>${filterRows}</tbody></table>

      <h2>Summary</h2>
      <div class="summary-grid">
        <div class="summary-card"><div class="label">Total studies</div><div class="value">${escapeHtml(summary.totalStudies)}</div></div>
        <div class="summary-card"><div class="label">Total reports delivered</div><div class="value">${escapeHtml(summary.totalReportsDelivered)}</div></div>
        <div class="summary-card"><div class="label">Average delivery time</div><div class="value">${escapeHtml(summary.averageDeliveryTimeFormatted)}</div></div>
        <div class="summary-card"><div class="label">Delayed reports</div><div class="value">${escapeHtml(summary.delayedReports)}</div></div>
      </div>

      <h2>Overall delivery time</h2>
      <table>
        <thead><tr><th>Date</th><th>Average delivery time</th></tr></thead>
        <tbody>${buildChartTableRows(chartPoints)}</tbody>
      </table>

      <h2>Modality breakdown</h2>
      <table>
        <thead><tr><th>Modality</th><th>Total reports</th><th>Avg delivery time</th></tr></thead>
        <tbody>${buildModalityTableRows(modalityRows)}</tbody>
      </table>

      <h2>Study delivery details</h2>
      <table class="detail-table">
        <thead><tr>
          <th>UHID</th>
          <th>Patient ID/ Visit ID</th>
          <th>Modality</th>
          <th>Study allocation Time</th>
          <th>Report Draft Time</th>
          <th>Approval Time</th>
          <th>Dispatch Time</th>
          <th>Total Delivery TAT</th>
          <th>End-to-End TAT</th>
        </tr></thead>
        <tbody>${buildDetailTableRows(detailRows)}</tbody>
      </table>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Builds a UTF-8 CSV (with BOM) for report delivery analytics.
 * Includes filter header, summary, daily chart series, and modality breakdown
 * (same columns as the former `exportModalityCsv`: Modality, Total reports, Avg delivery time).
 * Values that may contain commas/quotes are CSV-escaped.
 */
export function buildReportDeliveryCsv(
  data: ReportDeliveryExportData,
  meta: ReportDeliveryExportMeta
): string {
  const lines: string[] = [];
  const { summary, modalityRows, chartPoints = [], detailRows = [] } = data;

  lines.push('Report Delivery Analytics');
  lines.push(`Report Period,${formatPeriodLabel(meta.startDate, meta.endDate)}`);
  lines.push(`Generated On,${formatGeneratedAt()}`);
  if (meta.facilityLabel) {
    lines.push(`Facility,${escapeCsv(meta.facilityLabel)}`);
  }
  if (meta.modalityLabel) {
    lines.push(`Modality,${escapeCsv(meta.modalityLabel)}`);
  }
  if (meta.priorityLabel) {
    lines.push(`Priority,${escapeCsv(meta.priorityLabel)}`);
  }
  if (meta.radiologistLabel) {
    lines.push(`Radiologist,${escapeCsv(meta.radiologistLabel)}`);
  }
  if (meta.deliveryChannelLabel) {
    lines.push(`Delivery Channel,${escapeCsv(meta.deliveryChannelLabel)}`);
  }

  lines.push('');
  lines.push('SUMMARY');
  lines.push('Metric,Value');
  lines.push(`Total studies,${summary.totalStudies}`);
  lines.push(`Total reports delivered,${summary.totalReportsDelivered}`);
  lines.push(`Average delivery time,${escapeCsv(summary.averageDeliveryTimeFormatted)}`);
  lines.push(`Delayed reports,${summary.delayedReports}`);

  lines.push('');
  lines.push('OVERALL DELIVERY TIME');
  lines.push('Date,Average delivery time (min)');
  if (!chartPoints.length) {
    lines.push('No chart data,');
  } else {
    chartPoints.forEach(point => {
      lines.push(`${escapeCsv(point.date)},${point.minutes}`);
    });
  }

  lines.push('');
  lines.push('MODALITY BREAKDOWN');
  lines.push('Modality,Total reports,Avg delivery time');
  if (!modalityRows.length) {
    lines.push('No data,,');
  } else {
    modalityRows.forEach(row => {
      lines.push(
        [escapeCsv(row.modality), row.totalReports, escapeCsv(row.avgDeliveryTimeFormatted)].join(
          ','
        )
      );
    });
  }

  lines.push('');
  lines.push('STUDY DELIVERY DETAILS');
  lines.push(DETAIL_CSV_HEADERS.join(','));
  buildDetailCsvRows(detailRows).forEach(line => lines.push(line));

  return `\uFEFF${lines.join('\r\n')}`;
}

/**
 * Exports report delivery analytics in the requested format.
 *
 * Replaces the former modality-only `exportModalityCsv` helper: modality columns are included
 * in the full dashboard export. CSV/Excel downloads use
 * `report-delivery-analytics-{startDate}_{endDate}.{csv|xls}` and always call
 * `URL.revokeObjectURL` after the click.
 *
 * @param format - `'csv'` / `'excel'` download a file via Blob + `URL.revokeObjectURL`;
 *   `'pdf'` opens a print window.
 * @param data - Dashboard snapshot ({@link ReportDeliveryExportData}).
 * @param meta - Active filter labels and date range ({@link ReportDeliveryExportMeta}).
 */
export function exportReportDeliveryAnalytics(
  format: AnalyticsExportFormat,
  data: ReportDeliveryExportData,
  meta: ReportDeliveryExportMeta
): void {
  const baseName = `report-delivery-analytics-${meta.startDate}_${meta.endDate}`;

  if (format === 'csv') {
    downloadBlob(`${baseName}.csv`, buildReportDeliveryCsv(data, meta), 'text/csv;charset=utf-8;');
    return;
  }

  if (format === 'excel') {
    const html = buildReportHtml(data, meta).replace(
      '<title>Report Delivery Analytics</title>',
      '<title>Report Delivery Analytics</title>\n<meta http-equiv="Content-Type" content="application/vnd.ms-excel; charset=UTF-8" />'
    );
    downloadBlob(`${baseName}.xls`, html, 'application/vnd.ms-excel;charset=utf-8;');
    return;
  }

  const summaryHtml = `
    <table><tbody>
      <tr><th>Total studies</th><td>${escapeHtml(data.summary.totalStudies)}</td></tr>
      <tr><th>Total reports delivered</th><td>${escapeHtml(data.summary.totalReportsDelivered)}</td></tr>
      <tr><th>Average delivery time</th><td>${escapeHtml(data.summary.averageDeliveryTimeFormatted)}</td></tr>
      <tr><th>Delayed reports</th><td>${escapeHtml(data.summary.delayedReports)}</td></tr>
    </tbody></table>
    <h2>Overall delivery time</h2>
    <table><thead><tr><th>Date</th><th>Average delivery time</th></tr></thead><tbody>${buildChartTableRows(data.chartPoints || [])}</tbody></table>
    <h2>Modality breakdown</h2>
    <table><thead><tr><th>Modality</th><th>Total reports</th><th>Avg delivery time</th></tr></thead><tbody>${buildModalityTableRows(data.modalityRows)}</tbody></table>
    <h2>Study delivery details</h2>
    <table class="detail-table"><thead><tr>
      <th>UHID</th><th>Patient ID/ Visit ID</th><th>Modality</th><th>Study allocation Time</th>
      <th>Report Draft Time</th><th>Approval Time</th><th>Dispatch Time</th>
      <th>Total Delivery TAT</th><th>End-to-End TAT</th>
    </tr></thead><tbody>${buildDetailTableRows(data.detailRows || [])}</tbody></table>
  `;
  openAnalyticsPrintWindow('Report Delivery Analytics', summaryHtml);
}
